import {
  useRef,
  useEffect,
  type ReactNode,
  type ComponentRef,
  memo,
  useCallback,
} from "react";
import gsap from "gsap";
import { Card } from "@/components/ui/card";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import CardLauncher from "@/components/Common/CardLauncher";
import { copyToClipboard } from "@/lib/clipboard";
import { useSidebar } from "@/hooks/use-sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useContainerWidth } from "@/hooks/use-container-width";
import { toast } from "sonner";
import ResultsPanel from "@/components/Layout/ResultsPanel";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { type TokenWithMonitoring } from "@/features/auth/hooks/useApiToken";
import { type MonitoringType } from "@/constants/monitoring";
import FloatingButton from "../Common/FloatingButton";

interface TwoColumnLayoutProps {
  // Main content
  children: ReactNode;

  // Results data

  data?: unknown;
  isLoading?: boolean;
  isEmptyResults?: boolean;
  error?: unknown;
  responseJSON?: string;

  // API configuration
  apiEndpoint: string;
  apiToken: string | null | undefined;
  isApiTokenLoading?: boolean;
  // Results configuration
  resultsTitle?: string;
  resultsDescription?: string | ReactNode;
  showResults: boolean;
  onShowResults: (show: boolean) => void;

  // Actions
  onLaunch: () => void;
  onCopyCode?: () => void;
  handleExportCSV: () => void;
  handleExportJSON: () => void;

  // Layout configuration
  showFloatingButton?: boolean;
  floatingButtonCondition?: boolean;
  leftPanelMinSize?: number;
  rightPanelMinSize?: number;
  onPanelResize?: (sizes: number[]) => void;
  requireStepValidation?: boolean;
  customValidation?: () => boolean;
  additionalHeaderContent?: ReactNode;
  bottomContent?: ReactNode;

  // Token selection
  showTokenDropdown?: boolean;
  onTokenSelect?: (token: TokenWithMonitoring) => void;
  monitoringType?: MonitoringType;
  selectedToken?: TokenWithMonitoring | null;
  availableTokens?: TokenWithMonitoring[];
  tokensLoading?: boolean;
}

function TwoColumnLayout({
  children,
  isLoading = false,
  isEmptyResults = false,
  error,
  responseJSON,
  apiEndpoint,
  apiToken,
  isApiTokenLoading,
  resultsTitle = "Results",
  resultsDescription,
  showResults,
  onShowResults,
  onLaunch,
  onCopyCode,
  handleExportCSV,
  handleExportJSON,
  showFloatingButton = true,
  floatingButtonCondition,
  leftPanelMinSize = 50,
  rightPanelMinSize = 30,
  onPanelResize,
  requireStepValidation = false,
  customValidation,
  additionalHeaderContent,
  bottomContent,
  showTokenDropdown = false,
  onTokenSelect,
  selectedToken,
  availableTokens,
  tokensLoading,
}: TwoColumnLayoutProps) {
  const { isExpanded } = useSidebar();
  const isMobile = useIsMobile();
  const [containerBreakpoints, containerRef] = useContainerWidth();

  const panelRef = useRef<ComponentRef<typeof ResizablePanel>>(null);
  const resultsPanelContainerRef = useRef<HTMLDivElement>(null);

  // Animate results panel when it becomes visible
  useEffect(() => {
    if (!showResults || !resultsPanelContainerRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        resultsPanelContainerRef.current!,
        { opacity: 0, x: 22 },
        {
          opacity: 1,
          x: 0,
          duration: 0.38,
          ease: "power3.out",
          clearProps: "transform,opacity",
        },
      );
    });
    return () => ctx.revert();
  }, [showResults]);

  // Memoize callback functions to prevent unnecessary re-renders
  const defaultCopyCode = useCallback(() => {
    copyToClipboard(responseJSON || "{}");
    toast.success("Copied to clipboard");
  }, [responseJSON]);

  const handlePanelResize = useCallback(
    (sizes: number[]) => {
      if (onPanelResize) {
        onPanelResize(sizes);
      } else if (sizes[1] < 5) {
        onShowResults(false);
      }
    },
    [onPanelResize, onShowResults],
  );

  const handleCloseResults = useCallback(() => {
    panelRef.current?.resize(0);
    onShowResults(false);
  }, [onShowResults]);

  const handleShowResults = useCallback(() => {
    onShowResults(true);
  }, [onShowResults]);

  // Calculate responsive dimensions inline without useless useMemo overhead
  const dimensions = (() => {
    if (isMobile) {
      return {
        cardLauncherWidth: "w-full",
        cardLauncherWidthValue: 0,
        mainContentMaxWidth: "max-w-full",
        mainContentMinWidth: "min-w-0",
      };
    }

    const isSmallScreen =
      containerBreakpoints.isExtraSmall || containerBreakpoints.isSmall;
    const cardLauncherWidthValue = isSmallScreen
      ? isExpanded
        ? 200
        : 220
      : isExpanded
        ? 300
        : 300;

    let mainContentMaxWidth = "max-w-[1040px]";
    let mainContentMinWidth = "min-w-[540px]";

    if (containerBreakpoints.isExtraSmall) {
      mainContentMaxWidth = "max-w-[400px]";
      mainContentMinWidth = "min-w-[300px]";
    } else if (containerBreakpoints.isSmall) {
      mainContentMaxWidth = "max-w-[500px]";
      mainContentMinWidth = "min-w-[400px]";
    } else if (containerBreakpoints.isMedium) {
      mainContentMaxWidth = "max-w-[650px]";
      mainContentMinWidth = "min-w-[500px]";
    }

    return {
      cardLauncherWidth: `min-w-[${cardLauncherWidthValue}px]`,
      cardLauncherWidthValue,
      mainContentMaxWidth,
      mainContentMinWidth,
    };
  })();

  // Static scroll styles
  const scrollStyles = {
    transform: "translateZ(0)",
    scrollBehavior: "auto" as const,
    overscrollBehavior: "contain" as const,
  };

  const mainContentStyle = isMobile
    ? undefined
    : { paddingRight: `${dimensions.cardLauncherWidthValue + 16}px` };

  const cardLauncherProps = {
    showLaunchButton: isMobile,
    onLaunch,
    apiEndpoint,
    apiToken: apiToken ?? "",
    requireStepValidation,
    customValidation,
    isApiTokenLoading,
    showTokenDropdown,
    onTokenSelect,
    selectedToken,
    availableTokens,
    tokensLoading,
  };

  const resultsPanelProps = {
    title: resultsTitle,
    description: resultsDescription,
    isLoading,
    error,
    isEmptyResults,
    responseJSON,
    onCopyCode: onCopyCode || defaultCopyCode,
    handleExportCSV,
    handleExportJSON,
    additionalHeaderContent,
  };

  // Early return for mobile layout
  if (isMobile) {
    return (
      <div className="h-screen p-3 pb-0" ref={containerRef}>
        <div
          className="scrollbar-thin relative h-full overflow-y-auto"
          style={scrollStyles}
        >
          <div className="flex flex-col gap-3">
            <CardLauncher {...cardLauncherProps} />

            {showResults ? (
              <ResultsPanel {...resultsPanelProps} />
            ) : (
              <Card className="h-auto bg-transparent !shadow-none">
                <div className="flex flex-col p-4">{children}</div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen pl-3" ref={containerRef}>
      <div
        className="scrollbar-thin relative h-full overflow-y-auto"
        style={scrollStyles}
      >
        {!showResults ? (
          // Single view layout
          <div className="relative flex min-h-screen">
            <aside
              className={`sticky top-0 left-0 z-10 self-start p-3 ${dimensions.cardLauncherWidth}`}
            >
              <CardLauncher {...cardLauncherProps} />
            </aside>

            <div
              className="flex w-full items-start justify-center"
              style={mainContentStyle}
            >
              <Card
                className={`h-auto bg-transparent !shadow-none ${dimensions.mainContentMaxWidth} ${dimensions.mainContentMinWidth} w-full`}
              >
                <div className="flex flex-col pt-4">
                  <div className="m-auto flex min-h-0 w-[85%] flex-col justify-between p-6 pb-0">
                    {children}
                    {bottomContent}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        ) : (
          // Results view layout
          <div className="flex h-full pr-3">
            <aside
              className={`sticky top-0 z-10 flex-shrink-0 self-start p-3 ${dimensions.cardLauncherWidth}`}
            >
              <CardLauncher {...cardLauncherProps} />
            </aside>

            <div className="flex min-w-0 flex-1">
              <ResizablePanelGroup
                direction="horizontal"
                onLayout={handlePanelResize}
                className="flex flex-row gap-[3px]"
              >
                <ResizablePanel defaultSize={50} minSize={leftPanelMinSize}>
                  <Card
                    className={`h-full bg-transparent !shadow-none ${dimensions.mainContentMinWidth}`}
                  >
                    <div className="flex min-h-0 flex-1 flex-col pt-4">
                      <div className="flex min-h-0 flex-1 flex-col p-6 px-0 pb-0">
                        {children}
                        {bottomContent}
                      </div>
                    </div>
                  </Card>
                </ResizablePanel>

                <div className="relative flex items-center">
                  <ResizableHandle withHandle />
                  <div className="absolute bottom-20 -left-8 z-[1000]">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <FloatingButton onClick={handleCloseResults}>
                          <i className="ri-arrow-right-long-line text-sm text-white"></i>
                        </FloatingButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Close Results</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                <ResizablePanel
                  defaultSize={30}
                  minSize={rightPanelMinSize}
                  ref={panelRef}
                >
                  <div className="h-full py-3" ref={resultsPanelContainerRef}>
                    <ResultsPanel {...resultsPanelProps} />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </div>
        )}
      </div>

      {/* Floating button for showing results */}
      {showFloatingButton && !showResults && floatingButtonCondition && (
        <div className="animate-float-in fixed right-0 bottom-20 z-999">
          <Tooltip>
            <TooltipTrigger>
              <FloatingButton onClick={handleShowResults}>
                <i className="ri-arrow-left-long-line text-sm text-white"></i>
              </FloatingButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Show Results</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default memo(TwoColumnLayout);
